


import { Marker } from "@react-google-maps/api";
import { useMemo, memo, useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import throttle from "lodash/throttle"; // throttle kullanımı
import { setHoveredUser } from "../../features/branchUserLocations/branchUserLocationsSlice";

// Default marker SVG (örneğin mor renk)
const defaultSvg = `
<svg width="42" height="42" viewBox="0 0 42 42" fill="none" xmlns="http://www.w3.org/2000/svg">
  <circle cx="21" cy="21" r="21" fill="#663399"/>
  <circle cx="21" cy="21" r="17" fill="white"/>
  <circle cx="21" cy="21" r="14" fill="#663399"/>
</svg>
`;

// Hover durumunda marker SVG (örneğin kırmızı renk)
const hoverSvg = `
<svg width="42" height="42" viewBox="0 0 42 42" fill="none" xmlns="http://www.w3.org/2000/svg">
  <circle cx="21" cy="21" r="21" fill="red"/>
  <circle cx="21" cy="21" r="17" fill="white"/>
  <circle cx="21" cy="21" r="14" fill="red"/>
</svg>
`;

// Manuel dönüşüm için Web Mercator projeksiyon fonksiyonu
function latLngToPoint(lat, lng, zoom) {
  const siny = Math.sin((lat * Math.PI) / 180);
  const clampedSiny = Math.min(Math.max(siny, -0.9999), 0.9999);
  const x = 256 * (0.5 + lng / 360);
  const y =
    256 *
    (0.5 - Math.log((1 + clampedSiny) / (1 - clampedSiny)) / (4 * Math.PI));
  const scale = Math.pow(2, zoom);
  return { x: x * scale, y: y * scale };
}

// markerHelpers.js
export const getMarkerIcon = (svg) => {
  const encoded = encodeURIComponent(svg)
    .replace(/'/g, "%27")
    .replace(/"/g, "%22");
  return {
    url: `data:image/svg+xml,${encoded}`,
    scaledSize: { width: 12, height: 12 },
  };
};

const UserLocationItem = memo(({ userData, hoverTimeout, mapRef, mapContainerRef }) => {
  const dispatch = useDispatch();
  // Yerel hover state'i; marker üzerine gelindiğinde true, çıkıldığında false.
  const [isHovered, setIsHovered] = useState(false);

  // isHovered state'ine göre marker icon'u belirliyoruz
  const icon = useMemo(() => getMarkerIcon(isHovered ? hoverSvg : defaultSvg), [isHovered]);

  // Throttled fonksiyon: Marker'ın lat/lng'sini, güncel harita center, zoom ve container boyutlarına göre
  // piksel koordinatına çevirir ve setHoveredUser action'ını dispatch eder.
  const throttledUpdateHoveredMarker = useCallback(
    throttle((userData) => {
      
      if (!mapRef.current || !mapContainerRef.current) return;
      const containerRect = mapContainerRef.current.getBoundingClientRect();
      const containerWidth = containerRect.width;
      const containerHeight = containerRect.height;
      const zoom = mapRef.current.getZoom();
      const center = mapRef.current.getCenter();
      const centerPoint = latLngToPoint(center.lat(), center.lng(), zoom);
      const markerPoint = latLngToPoint(userData.latitude, userData.longitude, zoom);
      const pixelX = markerPoint.x - centerPoint.x + containerWidth / 2;
      const pixelY = markerPoint.y - centerPoint.y + containerHeight / 2;
      dispatch(setHoveredUser({ ...userData, x: pixelX, y: pixelY }));
    }, 500),
    [mapRef, mapContainerRef, dispatch]
  );

  // Marker üzerine gelindiğinde 500ms bekleyip hem hover state'ini güncelliyor hem de overlay hesaplamasını tetikliyoruz.
  const handleMarkerMouseEnter = useCallback(
    (e, userData) => {
      // Hover state'i hemen true yapıyoruz
      setIsHovered(true);
      if (hoverTimeout.current) clearTimeout(hoverTimeout.current);
      hoverTimeout.current = setTimeout(() => {
        throttledUpdateHoveredMarker(userData);
      }, 500);
    },
    [throttledUpdateHoveredMarker, hoverTimeout]
  );

  // Marker'dan çıkıldığında 500ms bekleyip hem hover state'ini false yapıyor hem de overlay'i kaldırıyoruz.
  const handleMarkerMouseLeave = useCallback(() => {
    setIsHovered(false);
    if (hoverTimeout.current) {
      clearTimeout(hoverTimeout.current);
      hoverTimeout.current = null;
    }
    hoverTimeout.current = setTimeout(() => {
      dispatch(setHoveredUser(null));
    }, 200);
  }, [dispatch, hoverTimeout]);

  return (
    <>
      <Marker
        position={{ lat: userData.latitude, lng: userData.longitude }}
        icon={icon}
        onMouseOver={(e) => handleMarkerMouseEnter(e, userData)}
        onMouseOut={handleMarkerMouseLeave}
      />
    </>
  );
});

export default UserLocationItem;
